import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, BookOpen, Check, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);

  // Availability helpers
  const isAvailable = course?.availability_status === 'disponible' || !course?.availability_status;
  const isComingSoon = course?.availability_status === 'proximamente';
  const isNotAvailable = course?.availability_status === 'no_disponible';
  const showPrice = !course?.hide_price;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, enrollmentRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          isAuthenticated ? api.get(`/enrollments/${courseId}`) : Promise.resolve({ data: { enrolled: false } })
        ]);
        setCourse(courseRes.data);
        setIsEnrolled(enrollmentRes.data.enrolled);
      } catch (error) {
        console.error('Error fetching course:', error);
        if (error.response?.status === 404) {
          navigate('/formaciones');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId, isAuthenticated, navigate]);

  const handleEnroll = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/checkout/${courseId}`);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[hsl(var(--text-secondary))]">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link 
            to="/formaciones" 
            className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-8 transition-colors"
            data-testid="course-back"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Volver a formaciones
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Course Image */}
            <div className="relative">
              <div className="aspect-video rounded-3xl overflow-hidden">
                <img
                  src={course.image_url || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=800&q=80'}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Course Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center gap-1 text-sm text-[hsl(var(--sage))] font-medium bg-[hsl(var(--sage-light))] px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4" strokeWidth={1.5} />
                  {course.duration}
                </span>
                {course.modules?.length > 0 && (
                  <span className="flex items-center gap-1 text-sm text-[hsl(var(--text-tertiary))] bg-[hsl(var(--sage-light))] px-3 py-1 rounded-full">
                    <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                    {course.modules.length} módulos
                  </span>
                )}
              </div>

              <h1 
                className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-3"
                data-testid="course-title"
              >
                {course.title}
              </h1>
              <p className="text-lg text-[hsl(var(--sage))] mb-6">
                {course.subtitle}
              </p>
              <p className="text-[hsl(var(--text-secondary))] leading-relaxed mb-8">
                {course.description}
              </p>

              {/* Price & CTA */}
              <div className="card-soft bg-gradient-to-br from-white to-[hsl(var(--sage-light))] p-6">
                {/* Status Badge */}
                {isComingSoon && (
                  <div className="mb-4 bg-amber-100 text-amber-700 px-4 py-2 rounded-lg text-center font-medium">
                    🕐 Próximamente disponible
                  </div>
                )}
                {isNotAvailable && (
                  <div className="mb-4 bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-center font-medium">
                    Este curso no está disponible actualmente
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[hsl(var(--text-secondary))]">Precio del curso</span>
                  {showPrice ? (
                    <span className="text-3xl font-heading text-[hsl(var(--accent))]" data-testid="course-price">
                      {course.price}€
                    </span>
                  ) : (
                    <span className="text-xl font-heading text-[hsl(var(--text-tertiary))] italic">
                      Por confirmar
                    </span>
                  )}
                </div>
                
                {isEnrolled ? (
                  <Link to={`/curso/${courseId}`} data-testid="course-access">
                    <Button className="btn-sage w-full flex items-center justify-center gap-2">
                      <Play className="w-5 h-5" strokeWidth={1.5} />
                      Acceder al curso
                    </Button>
                  </Link>
                ) : isAvailable ? (
                  <Button 
                    onClick={handleEnroll}
                    className="btn-gold w-full"
                    data-testid="course-enroll"
                  >
                    Inscribirme ahora
                  </Button>
                ) : isComingSoon ? (
                  <Button 
                    disabled
                    className="w-full bg-amber-100 text-amber-700 cursor-not-allowed"
                  >
                    Muy pronto
                  </Button>
                ) : (
                  <Button 
                    disabled
                    className="w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                  >
                    No disponible
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Long Description */}
      {course.long_description && (
        <section className="py-16 bg-[hsl(var(--sage-light))]">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-6">
              Sobre este curso
            </h2>
            <div className="prose prose-lg text-[hsl(var(--text-secondary))]">
              <p className="leading-relaxed whitespace-pre-line">
                {course.long_description}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Modules */}
      {course.modules?.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-8">
              Contenido del curso
            </h2>
            <div className="space-y-4">
              {course.modules.map((module, index) => (
                <div 
                  key={module.id || index}
                  className="card-soft p-6 flex items-start gap-4"
                  data-testid={`module-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[hsl(var(--accent))] font-medium">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-[hsl(var(--text-primary))] mb-1">
                      {module.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--text-secondary))]">
                      {module.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[hsl(var(--text-tertiary))]">
                    <span>{module.duration}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What you'll learn */}
      <section className="py-16 bg-[hsl(var(--sage-light))]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-8">
            Lo que aprenderás
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Técnicas prácticas que podrás aplicar desde el primer día',
              'Herramientas para tu autoconocimiento y sanación',
              'Acompañamiento claro y cercano durante todo el proceso',
              'Material de apoyo y recursos adicionales'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={2} />
                </div>
                <p className="text-[hsl(var(--text-secondary))]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {!isEnrolled && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-2xl font-heading text-[hsl(var(--text-primary))] mb-4">
              ¿Listo para comenzar?
            </h2>
            <p className="text-[hsl(var(--text-secondary))] mb-8">
              Únete a este camino de aprendizaje y transformación.
            </p>
            <Button 
              onClick={handleEnroll}
              className="btn-gold"
              data-testid="course-enroll-bottom"
            >
              Inscribirme por {course.price}€
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default CourseDetail;
